import { Fragment, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PmiButton } from 'shell/components/shared/button';
import Table from 'shell/components/shared/table';
import { HeadCell } from 'shell/components/shared/table/types';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { noop } from 'shell/utils/noop';
import {
  NotificationErrorVM,
  ProprietarioNotificaErrore,
} from 'practice/view-models/notification-error';
import ModalNotificationChoice from './modal-choice';
import ModalPrintSuccess from '../print-comunication/modal';

export const headCells: HeadCell<NotificationErrorVM, keyof NotificationErrorVM>[] = [
  {
    id: 'fiscalCode',
    label: 'CF',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'action',
    label: 'Azioni',
  },
];

interface NotificationErrorsTableProps {
  data: NotificationErrorVM[];
  onReload: () => void;
}

const NotificationErrorsTable = ({
  data,
  onReload,
}: NotificationErrorsTableProps): React.ReactElement => {
  const params = useParams();
  const castedParams = params as {
    codiceEnte: string;
    progressivoPratica: string;
    tipoRegistro: string;
    idAnnoPratica: string;
  };

  const [tableState, setTableState] = useState<{ page: number; rowsPerPage: number }>({
    page: 0,
    rowsPerPage: 10,
  });

  const [choiceModal, setChoiceModal] = useState<{
    open: boolean;
    owner?: ProprietarioNotificaErrore;
  }>({ open: false });

  const [pdfModal, setPdfModal] = useState<{ open: boolean; data: string; name: string }>({
    open: false,
    data: '',
    name: '',
  });

  const [printSuccessModal, setPrintSuccessModal] = useState<{ open: boolean }>({ open: false });

  const manualNotification = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/accertamento-messi/stampa-notifica-manuale`,
    {
      options: { method: 'POST' },
    }
  );

  const digitalNotification = useHttpClient(
    `${__PRACTICE_API_URL__}/proprietario/invio-notifica-digitale`,
    {
      options: { method: 'POST' },
    }
  );

  const handleChangePage = useCallback((newPage: number) => {
    setTableState((oldState) => ({ ...oldState, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((rowsPerPage: number) => {
    setTableState((oldState) => ({ ...oldState, page: 0, rowsPerPage }));
  }, []);

  const onNotifyPress = useCallback((owner: ProprietarioNotificaErrore) => {
    setChoiceModal({ open: true, owner });
  }, []);

  /**
   * Manual notification: print the notification (the backend already persists
   * the attachment - id allegato 12 - and sets fg_stato_notifica = 'M'), show
   * the produced PDF and, once closed, ask the operator to confirm the print.
   */
  const handleManual = useCallback(() => {
    const { owner } = choiceModal;

    if (!owner) {
      return;
    }

    setChoiceModal({ open: false });

    (
      manualNotification({
        responseType: 'blob',
        data: {
          codiceEnte: castedParams.codiceEnte || undefined,
          tipoRegistro: castedParams.tipoRegistro || undefined,
          idAnnoPratica: castedParams.idAnnoPratica
            ? Number.parseInt(castedParams.idAnnoPratica, 10)
            : undefined,
          progressivoPratica: castedParams.progressivoPratica
            ? Number.parseInt(castedParams.progressivoPratica, 10)
            : undefined,
          tipoEsito: '1',
          proprietari: [{ ...owner, flagRichiestaNotifica: '1' }],
        },
      }) as ResponsePromise<Blob>
    ).then((response) => {
      const pdfCertificate = response?.data;

      if (pdfCertificate) {
        const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
        blobToBase64(blob).then((base64) => {
          setPdfModal({
            data: base64.split(',')[1],
            name: 'Notifica',
            open: true,
          });
        });
      }
    });
  }, [choiceModal.owner, castedParams, manualNotification]);

  /**
   * Digital notification (SEND): forward the whole list element to the backend,
   * which handles the whole digital flow, then reload the list.
   */
  const handleDigital = useCallback(() => {
    const { owner } = choiceModal;

    if (!owner) {
      return;
    }

    setChoiceModal({ open: false });

    (digitalNotification({ data: owner }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');
      onReload();
    });
  }, [choiceModal.owner, digitalNotification, onReload]);

  const body = useMemo(() => {
    return data.map((el) => ({
      ...el,
      action: (
        <PmiButton
          variant="outlined"
          color="cdmBrandThird"
          onClick={() => onNotifyPress(el.proprietarioBE)}
          id={`notify-${el.id}`}
        >
          Notifica
        </PmiButton>
      ),
    }));
  }, [data, onNotifyPress]);

  return (
    <Fragment>
      <Table
        onSort={noop}
        head={headCells}
        orders={{}}
        page={tableState.page}
        rowsPerPage={tableState.rowsPerPage}
        body={body}
        count={data.length}
        noSort
        hideButtons
        isNotLayoutFixed
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ariaLabel="Errori richieste di notifica"
      />
      <ModalNotificationChoice
        open={choiceModal.open}
        onClose={() => setChoiceModal({ open: false })}
        onManual={handleManual}
        onDigital={handleDigital}
      />
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data}
        name={pdfModal.name}
        title="Notifica"
        onClose={() => {
          setPdfModal((oldState) => ({ ...oldState, open: false }));
          setPrintSuccessModal({ open: true });
        }}
      />
      {printSuccessModal.open ? (
        <ModalPrintSuccess
          open={printSuccessModal.open}
          onYes={() => {
            setPrintSuccessModal({ open: false });
            onReload();
          }}
          onClose={() => {
            setPrintSuccessModal({ open: false });
            setPdfModal((oldState) => ({ ...oldState, open: true }));
          }}
        />
      ) : undefined}
    </Fragment>
  );
};

NotificationErrorsTable.displayName = 'Accordion Notification Errors Table';

export default NotificationErrorsTable;
